import React, { FC, memo, useState, useCallback } from 'react';
import ProductDetailsSharedLayout from '../layout/shared-layout.component';
import ProductSetItem from './product-set-item/product-set-item.component';
import SubmitButton from './submit-button/submit-button.component';
import { useAlgoliaAnalytics, useNotification } from '~/shared/hooks';
import { M132ProductSetDetailModule, ProductSetViewModel, ProductSize } from '~/lib/data-contract';
import { useBasketAdd } from '~/features/basket';
import { useTranslation } from '~/shared/utils';

import qs from 'qs';

type Props = M132ProductSetDetailModule & {
    productSet: ProductSetViewModel
};

const ProductSetDetails: FC<Props> = memo(({ productSet }) => {
    const { add, isLoading } = useBasketAdd();
    const { push } = useNotification();
    const { translate } = useTranslation();
    const { trackAddToBasket } = useAlgoliaAnalytics();

    const {
        images,
        accordions,
        productSetName,
        priceDetails,
        products = [],
        requirePurchaseAsSet = false,
    } = productSet || {};

    const [selectedVariants, setSelectedVariants] = useState<
        { size: ProductSize; productColorId: string }[] | null
    >(null);
    const [canSubmit, setCanSubmit] = useState(false);
    const [sizeSelectWarning, setSizeSelectWarning] = useState<boolean>(false);
    const [isAddingToBasket, setIsAddingToBasket] = useState(false);

    const handleVariantSelect = useCallback(
        (size: ProductSize, productColorId: string) => {
            setSelectedVariants((prevSelectedVariants) => {
                const updatedVariants = prevSelectedVariants ? [...prevSelectedVariants] : [];
                const variantIndex = updatedVariants.findIndex(
                    (v) => v.productColorId === productColorId
                );
                if (variantIndex > -1) {
                    updatedVariants[variantIndex] = { size, productColorId };
                } else {
                    updatedVariants.push({ size, productColorId });
                }

                // Check if submission requirements are met with the updated variants
                let canSubmitNow = false;
                if (updatedVariants.length > 0) {
                    if (requirePurchaseAsSet) {
                        // If purchase as set is required, all products must have selected sizes
                        const allProductColorIds = products
                            .map((product) => product.productColorId)
                            .filter((colorId): colorId is string => Boolean(colorId));
                        const selectedProductColorIds = updatedVariants.map(
                            (variant) => variant.productColorId
                        );

                        canSubmitNow = allProductColorIds.every((colorId) =>
                            selectedProductColorIds.includes(colorId)
                        );
                    } else {
                        // If purchase as set is not required, at least one product must have a selected size
                        canSubmitNow = updatedVariants.length > 0;
                    }
                }

                setCanSubmit(canSubmitNow);
                return updatedVariants;
            });
        },
        [requirePurchaseAsSet, products]
    );

    const addToBasket = useCallback(async () => {
        if (!canSubmit) {
            setSizeSelectWarning(false);
            window.setTimeout(() => {
                setSizeSelectWarning(true);
            }, 100);
            return;
        }
        if (!selectedVariants || selectedVariants.length === 0) {
            push({ severity: 'warning', text: translate('message.fatalError') });
            return;
        }

        setIsAddingToBasket(true);

        const { hash } = window.location;
        const params = qs.parse(hash.slice(1));
        try {
            for (const variant of selectedVariants) {
                if (params.queryId) {
                    trackAddToBasket(
                        [variant.productColorId.toString()],
                        params.queryId.toString()
                    );
                }

                await add({
                    productId: variant.productColorId || '',
                    variantId: variant.size.variantId || '',
                    quantity: 1,
                    pageViewId: window.EasySizeParametersDebug?.easysize?.pageview_id || '',
                    userId: window.EasySizeParametersDebug?.easysize?.user_id || '',
                });
            }
        } catch (error) {
            console.error('Error adding to basket:', error);
            push({ severity: 'error', text: translate('message.fatalError') });
        } finally {
            setIsAddingToBasket(false);
            setSizeSelectWarning(false);
        }
    }, [canSubmit, selectedVariants, push, translate, add, trackAddToBasket]);

    const firstProduct = products[0];
    const subtitle = firstProduct ? firstProduct.subtitle : '';
    const isUpdatingBasket = isLoading || isAddingToBasket;

    return (
        <>
            <ProductDetailsSharedLayout
                productData={{
                    images,
                    accordions,
                    title: productSetName,
                    subtitle,
                    priceDetails,
                }}
            >
                {products.map((item) => (
                    <ProductSetItem
                        item={item}
                        key={item.productId}
                        onSelectVariant={handleVariantSelect}
                        sizeSelectWarning={sizeSelectWarning && !canSubmit}
                        selectedSize={
                            selectedVariants?.find((v) => v.productColorId === item.productColorId)
                                ?.size
                        }
                    />
                ))}
                <SubmitButton
                    selectedCount={selectedVariants?.length || 0}
                    canSubmit={canSubmit}
                    isUpdatingBasket={isUpdatingBasket}
                    onClick={addToBasket}
                />
            </ProductDetailsSharedLayout>
        </>
    );
});

ProductSetDetails.displayName = 'ProductSetDetails';
export default ProductSetDetails;
